/**
 * PDF.js helpers for the consent document builder + fill-and-sign popup.
 *
 * `pdfjs-dist` touches browser globals (`DOMMatrix`, etc.) at module-eval, which breaks
 * jsdom unit tests that merely import a component transitively pulling this file. So pdfjs
 * is loaded **lazily** (dynamic import on first use) — importing this module has no browser
 * side-effect, and the render path is only ever called after a PDF is actually loaded.
 *
 * The parser worker is bundled via Vite's `?worker` import (NOT `?url`): Vite compiles it
 * into a normal hashed `.js` chunk and we hand pdf.js the resulting Worker as a `workerPort`.
 * The earlier `?url` form emitted the worker as a raw `.mjs` asset, and dev-practice-v2's
 * static host serves `.mjs` as `application/octet-stream` — strict module-script MIME checking
 * then rejects it ("non-JavaScript MIME type"), so pdf.js silently fell back to a main-thread
 * "fake worker". `?worker` keeps it a `.js` (served correctly) and a real worker (PROD-1051).
 */
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { getBlob } from '@/shared/api/api-client';

export type PdfDocument = PDFDocumentProxy;
export type PdfPage = PDFPageProxy;

/**
 * Load a document-mode template's PDF by id, via the BE stream endpoint
 * `GET /api/ConsentTemplate/{id}/pdf` (PROD-1051). This is same-origin + authed (the
 * api-client adds the Bearer + org headers and the Vite proxy keeps it on our origin),
 * so it avoids the CORS block that fetching the raw Azure blob SAS URL hits in the browser
 * — and the BE re-derives the blob server-side, so an expired SAS still serves.
 */
export async function loadTemplatePdf(templateId: string, signal?: AbortSignal): Promise<PdfDocument> {
  const blob = await getBlob(`/api/ConsentTemplate/${templateId}/pdf`, { signal });
  return loadPdf(await blob.arrayBuffer());
}

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;
function getPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const pdfjs = await import('pdfjs-dist');
      // Vite bundles the worker as a `.js` chunk; instantiate it and pass it as the
      // workerPort so pdf.js never fetches a `.mjs` (see the file header for the MIME bug).
      const PdfWorker = (await import('pdfjs-dist/build/pdf.worker.min.mjs?worker')).default;
      pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
      return pdfjs;
    })();
  }
  return pdfjsPromise;
}

/** Load a PDF from a URL (signed Azure-blob URL) or raw bytes. */
export async function loadPdf(source: string | ArrayBuffer): Promise<PdfDocument> {
  const pdfjs = await getPdfjs();
  const task =
    typeof source === 'string'
      ? pdfjs.getDocument({ url: source })
      : pdfjs.getDocument({ data: source });
  return task.promise;
}

/**
 * Render one page onto a canvas at a fit-to-width scale. Returns the CSS pixel size of the
 * rendered page box so the overlay can map fractional `fieldMap` coords ↔ pixels.
 */
export async function renderPageToCanvas(
  page: PdfPage,
  canvas: HTMLCanvasElement,
  containerWidth: number,
): Promise<{ width: number; height: number }> {
  const base = page.getViewport({ scale: 1 });
  const scale = containerWidth / base.width;
  const viewport = page.getViewport({ scale });

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d canvas context unavailable');

  // Render at device pixel ratio for crispness, but lay the canvas out at CSS pixels.
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.floor(viewport.width * dpr);
  canvas.height = Math.floor(viewport.height * dpr);
  canvas.style.width = `${Math.floor(viewport.width)}px`;
  canvas.style.height = `${Math.floor(viewport.height)}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // pdfjs-dist@6 requires `canvas` alongside `canvasContext` in RenderParameters.
  await page.render({ canvas, canvasContext: ctx, viewport }).promise;
  return { width: viewport.width, height: viewport.height };
}
